'use client';

/** ADS — same APIs as classic /ads: accounts → campaigns → insights bundle. Hard-scoped via project.adsAccountId. */
import { useEffect, useState } from 'react';
import type { BusinessProject } from '@/lib/business-projects';
import { Glass, Kpi, Pill, StatusChip, EmptyState, mono, display } from '@/future/primitives';

type Account = { id: string; name: string; currency: string; lastScore?: number };
type Campaign = { id: string; name: string; status: string };
type Bundle = {
  account: { id: string; name: string; currency: string };
  period: { since: string; until: string };
  timeseries: { date: string; spend: number; impressions: number; clicks: number; ctr: number; cpc: number; cpm: number }[];
};

export function AdsSurface({ project }: { project: BusinessProject | null }) {
  const only = project?.adsAccountId;
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [selected, setSelected] = useState<string | null>(only ?? null);
  const [campaigns, setCampaigns] = useState<Campaign[] | null>(null);
  const [bundle, setBundle] = useState<Bundle | null>(null);

  useEffect(() => {
    fetch('/api/ads/accounts').then(r => r.json())
      .then(d => {
        const list: Account[] = (d.accounts || []).filter((a: Account) => !only || a.id === only);
        setAccounts(list);
        setSelected(s => s ?? list[0]?.id ?? null);
      })
      .catch(() => setAccounts([]));
  }, [only]);

  useEffect(() => {
    if (!selected) return;
    let live = true;
    setCampaigns(null); setBundle(null);
    fetch(`/api/ads/campaigns/${selected}`).then(r => r.json())
      .then(d => { if (live) setCampaigns(d.campaigns || []); }).catch(() => live && setCampaigns([]));
    fetch(`/api/ads/insights/${selected}?range=30d`).then(r => r.json())
      .then(d => { if (live) setBundle(d.bundle || null); }).catch(() => live && setBundle(null));
    return () => { live = false; };
  }, [selected]);

  if (accounts === null) return <div className="pt-10 text-center" style={{ ...mono(18, '.3em'), color: 'var(--uif-t5)' }}>SYNCING AD ACCOUNTS…</div>;
  if (!accounts.length) return <EmptyState what="NO AD ACCOUNT" action="No Meta ad account is attached to this project." />;

  const acct = accounts.find(a => a.id === selected);
  const ts = bundle?.timeseries || [];
  const sum = (k: 'spend' | 'impressions' | 'clicks') => ts.reduce((s, r) => s + (r[k] || 0), 0);
  const spend = sum('spend'), clicks = sum('clicks'), impr = sum('impressions');
  const ctr = impr ? clicks / impr * 100 : 0, cpc = clicks ? spend / clicks : 0;
  const maxSpend = Math.max(...ts.map(r => r.spend), 0.01);

  return (
    <div className="mx-auto max-w-5xl flex flex-col gap-4">
      {/* account row (multiple only in unscoped mode) */}
      <div className="flex items-center gap-2 flex-wrap">
        {accounts.map(a => (
          <Pill key={a.id} onClick={() => setSelected(a.id)} active={a.id === selected}
            label={`${a.name.toUpperCase()}${a.lastScore != null ? ` · ${a.lastScore}` : ''}`} />
        ))}
        <div className="ml-auto" style={{ ...mono(16, '.2em'), color: 'var(--uif-t5)' }}>
          {bundle ? `${bundle.period.since} → ${bundle.period.until}` : 'LAST 30D'}
        </div>
      </div>

      {/* KPI slab */}
      <div className="grid grid-cols-5 gap-3">
        <Kpi label="SPEND · 30D" value={`$${spend.toFixed(0)}`} sub={acct?.currency || ''} />
        <Kpi label="IMPRESSIONS" value={impr.toLocaleString()} />
        <Kpi label="CLICKS" value={clicks.toLocaleString()} />
        <Kpi label="CTR" value={`${ctr.toFixed(2)}%`} />
        <Kpi label="CPC" value={`$${cpc.toFixed(2)}`} />
      </div>

      {/* spend sparkline as vertical bars */}
      {ts.length > 0 && (
        <Glass className="p-5">
          <div style={{ ...mono(16, '.3em'), color: 'var(--uif-t5)' }}>DAILY SPEND</div>
          <div className="mt-3 flex items-end gap-1" style={{ height: 90 }}>
            {ts.map(r => (
              <div key={r.date} className="flex-1 rounded-t-sm" title={`${r.date} · $${r.spend.toFixed(2)}`}
                style={{ height: `${Math.max(3, r.spend / maxSpend * 100)}%`, background: 'linear-gradient(180deg, rgba(126,205,231,.55), rgba(126,205,231,.12))' }} />
            ))}
          </div>
        </Glass>
      )}

      {/* campaigns */}
      <Glass className="p-5">
        <div style={{ ...mono(16, '.3em'), color: 'var(--uif-t5)' }}>CAMPAIGNS</div>
        <div className="mt-3 flex flex-col gap-2">
          {campaigns === null && <div style={{ ...mono(18), color: 'var(--uif-t5)' }}>LOADING…</div>}
          {campaigns?.length === 0 && <div style={{ ...mono(18), color: 'var(--uif-t5)' }}>NO CAMPAIGNS</div>}
          {campaigns?.map(c => (
            <div key={c.id} className="flex items-center gap-3 py-1.5" style={{ borderBottom: '1px solid rgba(255,255,255,.04)' }}>
              <StatusChip label={c.status} kind={c.status === 'ACTIVE' ? 'good' : 'dim'} />
              <div style={{ ...display(24), color: 'var(--uif-t2)' }}>{c.name}</div>
            </div>
          ))}
        </div>
      </Glass>
    </div>
  );
}
